import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Zap } from 'lucide-react';

export default function AdminLogs() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
        if (!authUser) { navigate('/map'); return; }
        const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
        if (!profile || profile.role !== 'admin') {
          navigate('/map');
        } else {
          setUser({ ...authUser, ...profile });
        }
      }).catch(() => navigate('/map'));
  }, [navigate]);

  const { data: logs = [] } = useQuery({
    queryKey: ['adminLogs'],
    queryFn: async () => {
      const adminLogs = await base44.entities.AdminLog.list('-created_date', 100);
      return adminLogs;
    },
    enabled: !!user?.id
  });

  const getActionLabel = (action) => {
    const labels = {
      'challenge_activate': '🟢 Activer Challenge',
      'challenge_close': '🟠 Fermer Challenge',
      'challenge_draw': '🎟️ Exécuter Tirage',
      'court_disable': '🔴 Désactiver Terrain',
      'user_reviewed': '👁️ Utilisateur Examiné'
    };
    return labels[action] || action;
  };

  if (!user) {
    return <div className="flex items-center justify-center min-h-screen text-white">Chargement...</div>;
  }

  return (
    <div className="bg-[#0A0B10] text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            className="text-[#B3B6C2] hover:text-white"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-black uppercase tracking-tight">Journal d'audit</h1>
          <Zap className="w-6 h-6 text-[#6E2BFF]" />
        </div>

        {/* Logs List */}
        <div className="grid gap-3">
          {logs.length === 0 ? (
            <Card className="bg-[#13151D] border-l-4 border-[#6E2BFF] p-6 text-center">
              <p className="text-[#B3B6C2]">Aucune action admin pour le moment</p>
            </Card>
          ) : (
            logs.map(log => (
              <Card
                key={log.id}
                className="bg-[#13151D] border-l-4 border-l-[#6E2BFF] p-4 hover:bg-[#1B1E28] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-[#6E2BFF] text-white text-xs font-bold">
                        {getActionLabel(log.action_type)}
                      </Badge>
                      <p className="text-xs text-[#B3B6C2]">{log.target_entity}</p>
                    </div>
                    <p className="text-sm text-[#B3B6C2] mb-2">
                      <span className="font-semibold text-white">ID:</span> {log.target_id}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-[#1B1E28]">
                      <div className="text-xs text-[#B3B6C2]">
                        <p>Par: {log.admin_user_id}</p>
                      </div>
                      <p className="text-xs text-[#B3B6C2]">
                        {new Date(log.timestamp).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}